import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/it';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronLeft,
    faChevronRight,
    faWrench,
    faClipboardCheck,
    faClipboardList,
    faCalendarCheck,
    faCalendarXmark,
    faBriefcase,
    faCheckCircle,
    faHourglassHalf,
    faExclamationTriangle,
    faArchive,
    faClock,
    faUsers,
    faMapMarkerAlt,
    faSpinner,
    faEye,
    faPlus,
    faUserMinus
} from '@fortawesome/free-solid-svg-icons';
import { selectAccountData } from '../redux/main/accountSlice';
import { useColorPalette } from '../hooks/useColorPalette';
import { apiService } from '../utils/apiService';
import { getNodeApiUrl } from '../config/api.config';

moment.locale('it');

const AdminDashboard = () => {
    const navigate = useNavigate();
    const account = useSelector(selectAccountData);
    const palette = useColorPalette();
    const accountId = account?._id;

    // Stati per le date
    const [maintenanceMonth, setMaintenanceMonth] = useState(moment());
    const [jobsDate, setJobsDate] = useState(moment());
    const [assistanceDate, setAssistanceDate] = useState(moment());
    const [contractExpirationMonth, setContractExpirationMonth] = useState(moment());
    const [absencesDate, setAbsencesDate] = useState(moment());

    // Stati per i dati
    const [maintenances, setMaintenances] = useState([]);
    const [overdueMaintenances, setOverdueMaintenances] = useState([]); // Attività scadute non svolte
    const [maintenanceFilter, setMaintenanceFilter] = useState('all'); // Filtro card: all|toSchedule|scheduled|closed|overdue
    const [showAllMaintenances, setShowAllMaintenances] = useState(false); // Espande la lista oltre le prime 10
    const [expiringContracts, setExpiringContracts] = useState([]);
    const [handledContracts, setHandledContracts] = useState([]); // Contratti gia' gestiti (rinnovo)
    const [showHandledContracts, setShowHandledContracts] = useState(false); // Toggle "Mostra anche gestiti"
    const [openRenewalMenuId, setOpenRenewalMenuId] = useState(null); // Riga con scelta esito aperta
    const [updatingRenewalId, setUpdatingRenewalId] = useState(null); // Riga in aggiornamento
    const [loadingExpiringContracts, setLoadingExpiringContracts] = useState(false);
    const [scheduledJobs, setScheduledJobs] = useState([]);
    const [absences, setAbsences] = useState([]);
    const [loadingAbsences, setLoadingAbsences] = useState(false);
    
    // Stati per assistenze (3 categorie)
    const [assistanceExpiring, setAssistanceExpiring] = useState([]); // Scadenza oggi
    const [assistanceCreated, setAssistanceCreated] = useState([]); // Create oggi
    const [assistancePlanned, setAssistancePlanned] = useState([]); // Pianificate per oggi
    const [activeAssistanceTab, setActiveAssistanceTab] = useState('expiring'); // Tab attiva

    // Stati di loading
    const [loadingMaintenances, setLoadingMaintenances] = useState(false);
    const [loadingAssistance, setLoadingAssistance] = useState(false);
    const [loadingJobs, setLoadingJobs] = useState(false);

    // Stili CSS per scrollbar personalizzata sempre visibile
    useEffect(() => {
        const styleId = 'admin-dashboard-scrollbar-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                .custom-scrollbar {
                    overflow-y: scroll !important;
                    scrollbar-width: thin;
                    scrollbar-color: #6b7280 #e5e7eb;
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 10px;
                    display: block !important;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #e5e7eb;
                    border-radius: 5px;
                    margin: 4px 0;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: linear-gradient(180deg, #9ca3af 0%, #6b7280 100%);
                    border-radius: 5px;
                    min-height: 40px;
                    box-shadow: inset 0 0 6px rgba(0,0,0,0.1);
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(180deg, #6b7280 0%, #4b5563 100%);
                }
                .custom-scrollbar::-webkit-scrollbar-corner {
                    background: transparent;
                }
            `;
            document.head.appendChild(style);
        }
        return () => {
            const styleElem = document.getElementById(styleId);
            if (styleElem) styleElem.remove();
        };
    }, []);

    // Fetch manutenzioni con actions pianificate nel mese selezionato
    useEffect(() => {
        if (!accountId) return;

        const fetchMaintenanceActivities = async () => {
            setLoadingMaintenances(true);
            try {
                const selectedYear = maintenanceMonth.year().toString();
                const selectedMonth = maintenanceMonth.month() + 1; // moment months are 0-indexed

                // Ottieni tutte le manutenzioni dell'account
                const maintenancesResponse = await apiService.get(
                    `${getNodeApiUrl()}/maintenances/list?accountId=${accountId}&length=1000`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'auth-token': localStorage.getItem('installamelo-auth-token')
                        }
                    }
                );
                
                // Ottieni appuntamenti per verificare quali sono chiusi (range ampio per il mese)
                let appointmentsByMaintenance = {};
                try {
                    const startOfMonth = maintenanceMonth.clone().startOf('month').toISOString();
                    const endOfMonth = maintenanceMonth.clone().endOf('month').toISOString();
                    
                    const appointmentsResponse = await apiService.get(
                        `${getNodeApiUrl()}/appointments/bydate?accountId=${accountId}&fromDate=${startOfMonth}&toDate=${endOfMonth}`,
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                'auth-token': localStorage.getItem('installamelo-auth-token')
                            }
                        }
                    );
                    
                    const allAppointments = appointmentsResponse?.data || [];
                    if (Array.isArray(allAppointments)) {
                        allAppointments.forEach(app => {
                            if (app.maintenanceId) {
                                if (!appointmentsByMaintenance[app.maintenanceId]) {
                                    appointmentsByMaintenance[app.maintenanceId] = [];
                                }
                                appointmentsByMaintenance[app.maintenanceId].push(app);
                            }
                        });
                    }
                } catch (appErr) {
                    console.warn('Errore nel recupero appuntamenti per manutenzioni:', appErr);
                }

                if (maintenancesResponse && maintenancesResponse.data) {
                    const allMaintenances = Array.isArray(maintenancesResponse.data) ? maintenancesResponse.data : 
                                           (maintenancesResponse.data.data ? maintenancesResponse.data.data : []);
                    
                    // Filtra le manutenzioni che hanno actions pianificate nel mese selezionato
                    const maintenanceActions = [];
                    // Attività scadute: di mesi precedenti al mese selezionato e non ancora chiuse.
                    const overdueActions = [];

                    // Un'attività è considerata chiusa anche in base allo stato (es. commessa
                    // fatturata → stato "Chiusa"), non solo se c'è un appuntamento chiuso.
                    const isClosedByStatus = (statusLabel) => {
                        const s = (statusLabel || '').toLowerCase();
                        return s.includes('chius') || s.includes('fatturat') || s.includes('archivi');
                    };

                    const selectedYearNum = parseInt(selectedYear);

                    allMaintenances.forEach(maintenance => {
                        if (maintenance.actions && Array.isArray(maintenance.actions)) {
                            maintenance.actions.forEach(action => {
                                // Confronta year e month dell'action con il mese selezionato
                                const actionYear = action.year?.toString();
                                const actionMonth = parseInt(action.month);
                                const actionYearNum = parseInt(actionYear);

                                const baseData = {
                                    ...action,
                                    maintenanceId: maintenance._id,
                                    maintenanceCode: maintenance.code,
                                    customerId: maintenance.customerId,
                                    customerLabel: maintenance.customerLabel,
                                    systemId: maintenance.systemId,
                                    systemLabel: maintenance.systemLabel,
                                    systemAddressLabel: maintenance.systemAddressLabel,
                                    systemTypes: maintenance.systemTypes || []
                                };

                                if (actionYear === selectedYear && actionMonth === selectedMonth) {
                                    // Verifica se c'è almeno un appuntamento chiuso per questa manutenzione
                                    const maintenanceAppointments = appointmentsByMaintenance[maintenance._id] || [];
                                    const isClosedByAppointment = maintenanceAppointments.some(app =>
                                        app.closingDetails && app.closingDetails.isClosed === true
                                    );
                                    const isClosed = isClosedByAppointment || isClosedByStatus(action.statusLabel);

                                    maintenanceActions.push({
                                        ...baseData,
                                        isClosed: isClosed // Flag per indicare se l'attività è chiusa
                                    });
                                } else if (
                                    !isNaN(actionYearNum) && !isNaN(actionMonth) &&
                                    (actionYearNum < selectedYearNum || (actionYearNum === selectedYearNum && actionMonth < selectedMonth)) &&
                                    !isClosedByStatus(action.statusLabel)
                                ) {
                                    // Attività scaduta di un mese precedente ancora da gestire
                                    overdueActions.push({
                                        ...baseData,
                                        isClosed: false,
                                        isOverdue: true
                                    });
                                }
                            });
                        }
                    });

                    setMaintenances(maintenanceActions);
                    setOverdueMaintenances(overdueActions);
                }
            } catch (error) {
                console.error('Errore nel recupero delle attività di manutenzione:', error);
                setMaintenances([]);
                setOverdueMaintenances([]);
            } finally {
                setLoadingMaintenances(false);
            }
        };

        fetchMaintenanceActivities();
    }, [accountId, maintenanceMonth]);

    // Fetch contratti di manutenzione in scadenza nel mese selezionato
    useEffect(() => {
        if (!accountId) return;

        const fetchExpiringContracts = async () => {
            setLoadingExpiringContracts(true);
            try {
                const startOfMonth = contractExpirationMonth.clone().startOf('month');
                const endOfMonth = contractExpirationMonth.clone().endOf('month');

                // Ottieni tutte le manutenzioni dell'account
                const maintenancesResponse = await apiService.get(
                    `${getNodeApiUrl()}/maintenances/list?accountId=${accountId}&length=1000`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'auth-token': localStorage.getItem('installamelo-auth-token')
                        }
                    }
                );

                if (maintenancesResponse && maintenancesResponse.data) {
                    const allMaintenances = Array.isArray(maintenancesResponse.data) ? maintenancesResponse.data : 
                                           (maintenancesResponse.data.data ? maintenancesResponse.data.data : []);
                    
                    // Filtra le manutenzioni con contractEndDate nel mese selezionato
                    const inMonth = allMaintenances.filter(m => {
                        if (!m.contractEndDate) return false;
                        const endDate = moment(m.contractEndDate);
                        return endDate.isBetween(startOfMonth, endOfMonth, 'day', '[]');
                    }).sort((a, b) => moment(a.contractEndDate).diff(moment(b.contractEndDate)));

                    // Separa i contratti gia' gestiti (rinnovo) da quelli ancora da gestire.
                    // Un contratto e' "gestito" quando ha contractRenewal.status valorizzato.
                    const expiring = inMonth.filter(m => !(m.contractRenewal && m.contractRenewal.status));
                    const handled = inMonth.filter(m => m.contractRenewal && m.contractRenewal.status);

                    setExpiringContracts(expiring);
                    setHandledContracts(handled);
                }
            } catch (error) {
                console.error('Errore nel recupero dei contratti in scadenza:', error);
                setExpiringContracts([]);
                setHandledContracts([]);
            } finally {
                setLoadingExpiringContracts(false);
            }
        };

        fetchExpiringContracts();
    }, [accountId, contractExpirationMonth]);

    // Segna un contratto come gestito (rinnovato / non rinnovato / gestito).
    // Aggiorna la manutenzione e ricarica la lista contratti in scadenza.
    const handleMarkContractRenewal = async (contractId, status) => {
        if (!contractId || !status) return;
        setUpdatingRenewalId(contractId);
        try {
            const authUser = JSON.parse(localStorage.getItem('installamelo-auth-user') || '{}');
            const userId = authUser.userId || null;
            const userLabel = localStorage.getItem('userLabel') || 'Utente';

            await apiService.patch(
                `${getNodeApiUrl()}/maintenances/${contractId}/contract-renewal`,
                { status, userId, userLabel },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': localStorage.getItem('installamelo-auth-token')
                    }
                }
            );

            // Sposta il contratto dalla lista "in scadenza" a quella "gestiti"
            setExpiringContracts(prev => prev.filter(c => c._id !== contractId));
            setHandledContracts(prev => {
                const moved = expiringContracts.find(c => c._id === contractId) || prev.find(c => c._id === contractId);
                const others = prev.filter(c => c._id !== contractId);
                if (!moved) return others;
                return [...others, {
                    ...moved,
                    contractRenewal: {
                        status,
                        handledDate: new Date().toISOString(),
                        handledByUserId: userId,
                        handledByLabel: userLabel
                    }
                }];
            });
            setOpenRenewalMenuId(null);
        } catch (error) {
            console.error('Errore nel salvataggio dello stato rinnovo contratto:', error);
        } finally {
            setUpdatingRenewalId(null);
        }
    };

    // Annulla la gestione di un contratto: rimuove contractRenewal e lo riporta
    // tra i contratti in scadenza.
    const handleUndoContractRenewal = async (contractId) => {
        if (!contractId) return;
        setUpdatingRenewalId(contractId);
        try {
            await apiService.delete(
                `${getNodeApiUrl()}/maintenances/${contractId}/contract-renewal`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': localStorage.getItem('installamelo-auth-token')
                    }
                }
            );

            setHandledContracts(prev => prev.filter(c => c._id !== contractId));
            setExpiringContracts(prev => {
                const moved = handledContracts.find(c => c._id === contractId);
                if (!moved) return prev;
                const restored = { ...moved };
                delete restored.contractRenewal;
                const next = [...prev, restored];
                return next.sort((a, b) => moment(a.contractEndDate).diff(moment(b.contractEndDate)));
            });
        } catch (error) {
            console.error('Errore nell\'annullamento dello stato rinnovo contratto:', error);
        } finally {
            setUpdatingRenewalId(null);
        }
    };

    // Etichetta leggibile per lo stato di rinnovo
    const renewalStatusLabel = (status) => {
        switch (status) {
            case 'rinnovato': return 'Rinnovato';
            case 'non_rinnovato': return 'Non rinnovato';
            case 'gestito': return 'Gestito';
            default: return status || '';
        }
    };

    // Fetch assenze utenti per la data selezionata
    useEffect(() => {
        if (!accountId) return;

        const fetchAbsences = async () => {
            setLoadingAbsences(true);
            try {
                const response = await apiService.get(
                    `${getNodeApiUrl()}/employeeAbsences?accountId=${accountId}`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'auth-token': localStorage.getItem('installamelo-auth-token')
                        }
                    }
                );

                if (response && response.data) {
                    const allAbsences = Array.isArray(response.data) ? response.data : [];
                    
                    // Filtra assenze che includono la data selezionata
                    const selectedDate = absencesDate.clone();
                    const absencesForDate = allAbsences.filter(absence => {
                        const start = moment(absence.start);
                        const end = moment(absence.end);
                        return selectedDate.isBetween(start, end, 'day', '[]');
                    });

                    setAbsences(absencesForDate);
                }
            } catch (error) {
                console.error('Errore nel recupero delle assenze:', error);
                setAbsences([]);
            } finally {
                setLoadingAbsences(false);
            }
        };

        fetchAbsences();
    }, [accountId, absencesDate]);

    // Fetch assistenze per il giorno selezionato (scadenza, create, pianificate)
    useEffect(() => {
        if (!accountId) return;

        const fetchAssistanceData = async () => {
            setLoadingAssistance(true);
            try {
                const dateParam = assistanceDate.format('YYYY-MM-DD');

                // Fetch assistenze (nuovo endpoint dedicato) e appuntamenti in parallelo
                const [assistanceResponse, appointmentsResponse] = await Promise.all([
                    apiService.get(
                        `${getNodeApiUrl()}/assistanceRequests/forDashboard?accountId=${accountId}&date=${dateParam}`,
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                'auth-token': localStorage.getItem('installamelo-auth-token')
                            }
                        }
                    ),
                    apiService.get(
                        `${getNodeApiUrl()}/appointments/forDashboard?accountId=${accountId}&date=${dateParam}`,
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                'auth-token': localStorage.getItem('installamelo-auth-token')
                            }
                        }
                    )
                ]);

                // Dati dal nuovo endpoint assistenze
                // apiService già estrae .data dalla risposta axios, quindi i dati sono direttamente in assistanceResponse
                const assistanceData = assistanceResponse?.data || assistanceResponse;
                const expiring = assistanceData?.expiring || [];
                const created = assistanceData?.created || [];
                
                console.log('[Dashboard Assistenze] assistanceData:', assistanceData);
                console.log('[Dashboard Assistenze] Dal server - Scadenza:', expiring.length, 'Create:', created.length);
                
                // Dati appuntamenti per le pianificate
                const appointmentsData = appointmentsResponse?.data?.data || appointmentsResponse?.data || [];
                const appointmentsArray = Array.isArray(appointmentsData) ? appointmentsData : [];
                
                console.log('[Dashboard Assistenze] Appuntamenti trovati:', appointmentsArray.length);
                
                // 3. Assistenze pianificate per il giorno selezionato
                // Creiamo oggetti assistenza dagli appuntamenti con assistanceRequestId
                const plannedFromAppointments = appointmentsArray
                    .filter(app => app.assistanceRequestId)
                    .map(app => {
                        // Cerca l'assistenza corrispondente tra quelle in scadenza o create
                        const allAssistances = [...expiring, ...created];
                        const matchingAssistance = allAssistances.find(a => 
                            (a._id?.toString() || a._id) === app.assistanceRequestId
                        );
                        
                        // Se trovata, usa i dati dell'assistenza, altrimenti usa i dati dell'appuntamento
                        if (matchingAssistance) {
                            return matchingAssistance;
                        } else {
                            // Fallback: crea un oggetto assistenza dai dati dell'appuntamento
                            return {
                                _id: app.assistanceRequestId,
                                code: app.assistanceRequestCode || 'N/D',
                                title: app.title || 'Assistenza pianificata',
                                customerLabel: app.customerLabel,
                                systemLabel: app.systemLabel,
                                statusLabel: 'Pianificata',
                                fromAppointment: true
                            };
                        }
                    });
                
                // Rimuovi duplicati per _id
                const planned = plannedFromAppointments.filter((item, index, self) =>
                    index === self.findIndex(t => (t._id?.toString() || t._id) === (item._id?.toString() || item._id))
                );
                
                console.log('[Dashboard Assistenze] Pianificate trovate:', planned.length);
                
                setAssistanceExpiring(expiring);
                setAssistanceCreated(created);
                setAssistancePlanned(planned);
            } catch (error) {
                console.error('Errore nel recupero delle assistenze:', error);
                setAssistanceExpiring([]);
                setAssistanceCreated([]);
                setAssistancePlanned([]);
            } finally {
                setLoadingAssistance(false);
            }
        };

        fetchAssistanceData();
    }, [accountId, assistanceDate]);

    // Fetch commesse del giorno (tramite appuntamenti)
    useEffect(() => {
        if (!accountId) return;

        const fetchScheduledJobs = async () => {
            setLoadingJobs(true);
            try {
                const dateParam = jobsDate.format('YYYY-MM-DD');

                // Usa il nuovo endpoint per la dashboard
                const response = await apiService.get(
                    `${getNodeApiUrl()}/appointments/forDashboard?accountId=${accountId}&date=${dateParam}`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'auth-token': localStorage.getItem('installamelo-auth-token')
                        }
                    }
                );

                const appointmentsData = response?.data?.data || response?.data || [];
                const appointments = Array.isArray(appointmentsData) ? appointmentsData : [];
                
                console.log('[Dashboard Commesse] Appuntamenti trovati:', appointments.length);
                
                // Filtra appuntamenti con jobId (sono quelli legati a commesse)
                const jobAppointments = appointments.filter(app => app.jobId);
                
                console.log('[Dashboard Commesse] Commesse schedulate:', jobAppointments.length);
                
                setScheduledJobs(jobAppointments);
            } catch (error) {
                console.error('Errore nel recupero delle commesse:', error);
                setScheduledJobs([]);
            } finally {
                setLoadingJobs(false);
            }
        };

        fetchScheduledJobs();
    }, [accountId, jobsDate]);

    // Classifica un'attività di manutenzione in: closed | scheduled | toSchedule
    // (usata sia per i conteggi che per il filtro delle card)
    const classifyMaintenance = (m) => {
        if (m.isClosed === true) return 'closed';
        const s = (m.statusLabel || '').toLowerCase();
        if (m.statusId === '1' || (s.includes('pianificat') && !s.includes('da pianificare'))) {
            return 'scheduled';
        }
        return 'toSchedule';
    };

    // Calcoli per le attività di manutenzione pianificate
    const maintenanceStats = useMemo(() => {
        const total = maintenances.length;
        const closed = maintenances.filter(m => classifyMaintenance(m) === 'closed').length;
        const scheduled = maintenances.filter(m => classifyMaintenance(m) === 'scheduled').length;
        const toSchedule = maintenances.filter(m => classifyMaintenance(m) === 'toSchedule').length;

        return {
            total,                              // Blu
            toSchedule,                         // Rosso
            scheduled,                          // Verde
            closed,                             // Arancione
            overdue: overdueMaintenances.length // Scaduti
        };
    }, [maintenances, overdueMaintenances]);

    // Lista attività di manutenzione filtrata in base alla card selezionata
    const filteredMaintenances = useMemo(() => {
        switch (maintenanceFilter) {
            case 'overdue':
                return overdueMaintenances;
            case 'toSchedule':
                return maintenances.filter(m => classifyMaintenance(m) === 'toSchedule');
            case 'scheduled':
                return maintenances.filter(m => classifyMaintenance(m) === 'scheduled');
            case 'closed':
                return maintenances.filter(m => classifyMaintenance(m) === 'closed');
            default:
                return maintenances;
        }
    }, [maintenanceFilter, maintenances, overdueMaintenances]);

    // Etichetta della card selezionata, usata nel pulsante "Vedi tutte le ..."
    const maintenanceFilterLabel = useMemo(() => {
        switch (maintenanceFilter) {
            case 'toSchedule': return 'da pianificare';
            case 'scheduled': return 'pianificate';
            case 'closed': return 'chiuse';
            case 'overdue': return 'scadute';
            default: return '';
        }
    }, [maintenanceFilter]);

    // Cambiando mese la lista torna compressa
    useEffect(() => {
        setShowAllMaintenances(false);
    }, [maintenanceMonth]);

    // Click su una card: attiva/disattiva il filtro corrispondente
    // Cambiando card si torna alla lista compressa (prime 10)
    const handleMaintenanceFilterClick = (filter) => {
        setMaintenanceFilter(prev => (prev === filter ? 'all' : filter));
        setShowAllMaintenances(false);
    };

    // Lista assistenze attiva in base alla tab selezionata
    const activeAssistanceList = useMemo(() => {
        switch (activeAssistanceTab) {
            case 'expiring':
                return assistanceExpiring;
            case 'created':
                return assistanceCreated;
            case 'planned':
                return assistancePlanned;
            default:
                return [];
        }
    }, [activeAssistanceTab, assistanceExpiring, assistanceCreated, assistancePlanned]);

    // Navigazione mesi per manutenzioni
    const handleMaintenancePrevMonth = () => {
        setMaintenanceMonth(prev => prev.clone().subtract(1, 'month'));
    };

    const handleMaintenanceNextMonth = () => {
        setMaintenanceMonth(prev => prev.clone().add(1, 'month'));
    };

    const handleMaintenanceToday = () => {
        setMaintenanceMonth(moment());
    };

    // Navigazione mesi per contratti in scadenza
    const handleContractExpirationPrevMonth = () => {
        setContractExpirationMonth(prev => prev.clone().subtract(1, 'month'));
    };

    const handleContractExpirationNextMonth = () => {
        setContractExpirationMonth(prev => prev.clone().add(1, 'month'));
    };

    const handleContractExpirationToday = () => {
        setContractExpirationMonth(moment());
    };

    // Navigazione giorni per commesse
    const handleJobsPrevDay = () => {
        setJobsDate(prev => prev.clone().subtract(1, 'day'));
    };

    const handleJobsNextDay = () => {
        setJobsDate(prev => prev.clone().add(1, 'day'));
    };

    const handleJobsToday = () => {
        setJobsDate(moment());
    };

    // Navigazione giorni per assistenze
    const handleAssistancePrevDay = () => {
        setAssistanceDate(prev => prev.clone().subtract(1, 'day'));
    };

    const handleAssistanceNextDay = () => {
        setAssistanceDate(prev => prev.clone().add(1, 'day'));
    };

    const handleAssistanceToday = () => {
        setAssistanceDate(moment());
    };

    // Navigazione giorni per assenze
    const handleAbsencesPrevDay = () => {
        setAbsencesDate(prev => prev.clone().subtract(1, 'day'));
    };

    const handleAbsencesNextDay = () => {
        setAbsencesDate(prev => prev.clone().add(1, 'day'));
    };

    const handleAbsencesToday = () => {
        setAbsencesDate(moment());
    };

    // Card Component
    const StatCard = ({ icon, title, value, color, subtext, onClick }) => (
        <div 
            className={`bg-white rounded-xl shadow-sm border border-gray-100 p-5 transition-all duration-300 hover:shadow-md ${onClick ? 'cursor-pointer hover:scale-[1.02]' : ''}`}
            onClick={onClick}
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-500 text-sm font-medium mb-1">{title}</p>
                    <p className="text-3xl font-bold" style={{ color }}>{value}</p>
                    {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
                </div>
                <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${color}15` }}
                >
                    <FontAwesomeIcon icon={icon} className="text-2xl" style={{ color }} />
                </div>
            </div>
        </div>
    );

    // Navigation Header Component
    const NavigationHeader = ({ title, subtitle, onPrev, onNext, onToday, icon }) => (
        <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
                <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${palette.primary}15` }}
                >
                    <FontAwesomeIcon icon={icon} style={{ color: palette.primary }} />
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
                    <p className="text-sm text-gray-500 capitalize">{subtitle}</p>
                </div>
            </div>
            <div className="flex items-center gap-2">
                {onToday && (
                    <button
                        onClick={onToday}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
                        style={{ 
                            backgroundColor: `${palette.primary}10`,
                            color: palette.primary
                        }}
                    >
                        Oggi
                    </button>
                )}
                <button
                    onClick={onPrev}
                    className="w-9 h-9 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                    <FontAwesomeIcon icon={faChevronLeft} className="text-gray-600" />
                </button>
                <button
                    onClick={onNext}
                    className="w-9 h-9 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                    <FontAwesomeIcon icon={faChevronRight} className="text-gray-600" />
                </button>
            </div>
        </div>
    );

    // Loading Spinner
    const LoadingSpinner = () => (
        <div className="flex items-center justify-center py-8">
            <FontAwesomeIcon icon={faSpinner} className="text-2xl text-gray-400 animate-spin" />
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-800">Dashboard Amministratore</h1>
                <p className="text-gray-500 mt-1">Panoramica delle attività e pianificazioni</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Box Assistenze del Giorno - PRIMA POSIZIONE */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <NavigationHeader
                        title="Assistenze"
                        subtitle={assistanceDate.format('dddd DD MMMM YYYY')}
                        onPrev={handleAssistancePrevDay}
                        onNext={handleAssistanceNextDay}
                        onToday={handleAssistanceToday}
                        icon={faClipboardList}
                    />

                    {loadingAssistance ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            {/* 3 Indicatori cliccabili */}
                            <div className="grid grid-cols-3 gap-3 mb-5">
                                <button
                                    onClick={() => setActiveAssistanceTab('expiring')}
                                    className={`p-4 rounded-xl text-center transition-all ${
                                        activeAssistanceTab === 'expiring'
                                            ? 'bg-red-100 border-2 border-red-400 shadow-sm'
                                            : 'bg-gradient-to-br from-red-50 to-red-100 border border-transparent hover:border-red-200'
                                    }`}
                                >
                                    <FontAwesomeIcon icon={faClock} className={`text-lg mb-1 ${activeAssistanceTab === 'expiring' ? 'text-red-600' : 'text-red-500'}`} />
                                    <p className="text-2xl font-bold text-red-600">{assistanceExpiring.length}</p>
                                    <p className="text-xs text-red-600/70 font-medium">In Scadenza</p>
                                </button>
                                <button
                                    onClick={() => setActiveAssistanceTab('created')}
                                    className={`p-4 rounded-xl text-center transition-all ${
                                        activeAssistanceTab === 'created'
                                            ? 'bg-blue-100 border-2 border-blue-400 shadow-sm'
                                            : 'bg-gradient-to-br from-blue-50 to-blue-100 border border-transparent hover:border-blue-200'
                                    }`}
                                >
                                    <FontAwesomeIcon icon={faPlus} className={`text-lg mb-1 ${activeAssistanceTab === 'created' ? 'text-blue-600' : 'text-blue-500'}`} />
                                    <p className="text-2xl font-bold text-blue-600">{assistanceCreated.length}</p>
                                    <p className="text-xs text-blue-600/70 font-medium">Create</p>
                                </button>
                                <button
                                    onClick={() => setActiveAssistanceTab('planned')}
                                    className={`p-4 rounded-xl text-center transition-all ${
                                        activeAssistanceTab === 'planned'
                                            ? 'bg-purple-100 border-2 border-purple-400 shadow-sm'
                                            : 'bg-gradient-to-br from-purple-50 to-purple-100 border border-transparent hover:border-purple-200'
                                    }`}
                                >
                                    <FontAwesomeIcon icon={faCalendarCheck} className={`text-lg mb-1 ${activeAssistanceTab === 'planned' ? 'text-purple-600' : 'text-purple-500'}`} />
                                    <p className="text-2xl font-bold text-purple-600">{assistancePlanned.length}</p>
                                    <p className="text-xs text-purple-600/70 font-medium">Pianificate</p>
                                </button>
                            </div>

                            {/* Lista assistenze */}
                            <div 
                                className="space-y-2 max-h-[280px] pr-3 custom-scrollbar"
                                style={{ overflowY: 'scroll', scrollbarWidth: 'thin', scrollbarColor: '#6b7280 #e5e7eb' }}
                            >
                                {activeAssistanceList.length === 0 ? (
                                    <div className="text-center py-8 text-gray-400">
                                        <FontAwesomeIcon icon={faClipboardList} className="text-3xl mb-2" />
                                        <p className="text-sm">
                                            {activeAssistanceTab === 'expiring' && 'Nessuna assistenza in scadenza'}
                                            {activeAssistanceTab === 'created' && 'Nessuna assistenza creata'}
                                            {activeAssistanceTab === 'planned' && 'Nessuna assistenza pianificata'}
                                        </p>
                                    </div>
                                ) : (
                                    activeAssistanceList.map((assistance, index) => (
                                        <div
                                            key={assistance._id || index}
                                            className={`p-3 rounded-lg border transition-all cursor-pointer hover:shadow-sm ${
                                                activeAssistanceTab === 'expiring'
                                                    ? 'bg-red-50 border-red-200 hover:border-red-300'
                                                    : activeAssistanceTab === 'created'
                                                    ? 'bg-blue-50 border-blue-200 hover:border-blue-300'
                                                    : 'bg-purple-50 border-purple-200 hover:border-purple-300'
                                            }`}
                                            onClick={() => navigate(`/assistanceRequestsNoAI/edit/${assistance._id}`)}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                    activeAssistanceTab === 'expiring' ? 'bg-red-200' :
                                                    activeAssistanceTab === 'created' ? 'bg-blue-200' : 'bg-purple-200'
                                                }`}>
                                                    <FontAwesomeIcon 
                                                        icon={activeAssistanceTab === 'expiring' ? faClock : activeAssistanceTab === 'created' ? faPlus : faCalendarCheck}
                                                        className={`text-sm ${
                                                            activeAssistanceTab === 'expiring' ? 'text-red-700' :
                                                            activeAssistanceTab === 'created' ? 'text-blue-700' : 'text-purple-700'
                                                        }`}
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-800 truncate">
                                                        {assistance.code || assistance.title || 'Assistenza'}
                                                    </p>
                                                    <p className="text-xs text-gray-500 truncate">
                                                        {assistance.customerLabel || 'Cliente N/D'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Box Assenze Utenti Oggi - SECONDA POSIZIONE */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <NavigationHeader
                        title="Assenze"
                        subtitle={absencesDate.format('dddd DD MMMM YYYY')}
                        onPrev={handleAbsencesPrevDay}
                        onNext={handleAbsencesNextDay}
                        onToday={handleAbsencesToday}
                        icon={faUserMinus}
                    />

                    {loadingAbsences ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            {/* Stats */}
                            <div className="grid grid-cols-1 gap-3 mb-5">
                                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 text-center">
                                    <p className="text-2xl font-bold text-gray-600">{absences.length}</p>
                                    <p className="text-xs text-gray-600/70 font-medium">Collaboratori assenti</p>
                                </div>
                            </div>

                            {/* Lista Assenze */}
                            <div 
                                className="space-y-2 max-h-[280px] pr-3 custom-scrollbar"
                                style={{ overflowY: 'scroll', scrollbarWidth: 'thin', scrollbarColor: '#6b7280 #e5e7eb' }}
                            >
                                {absences.length === 0 ? (
                                    <div className="text-center py-8 text-gray-400">
                                        <FontAwesomeIcon icon={faUsers} className="text-3xl mb-2" />
                                        <p className="text-sm">Nessuna assenza per questa data</p>
                                    </div>
                                ) : (
                                    absences.map((absence, index) => (
                                        <div
                                            key={absence._id || index}
                                            className="p-3 rounded-lg border bg-gray-50 border-gray-200 hover:border-gray-300 transition-all"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                                    <FontAwesomeIcon icon={faUserMinus} className="text-sm text-gray-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-800 truncate">
                                                        {absence.userName || absence.userLabel || 'Collaboratore'}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {absence.absenceType || 'Assenza'} • {moment(absence.start).format('HH:mm')} - {moment(absence.end).format('HH:mm')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Box Attività Manutenzione Pianificate del Mese - TERZA POSIZIONE */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <NavigationHeader
                        title="Attività Manutenzione Pianificate"
                        subtitle={maintenanceMonth.format('MMMM YYYY')}
                        onPrev={handleMaintenancePrevMonth}
                        onNext={handleMaintenanceNextMonth}
                        onToday={handleMaintenanceToday}
                        icon={faWrench}
                    />

                    {loadingMaintenances ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            {/* Stats Cards cliccabili - filtro per stato */}
                            <div className="grid grid-cols-5 gap-2 mb-5">
                                <button
                                    onClick={() => handleMaintenanceFilterClick('all')}
                                    className={`bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 text-center transition-all ${maintenanceFilter === 'all' ? 'ring-2 ring-blue-400' : 'hover:ring-2 hover:ring-blue-200'}`}
                                >
                                    <p className="text-2xl font-bold text-blue-600">{maintenanceStats.total}</p>
                                    <p className="text-xs text-blue-600/70 font-medium">Totali</p>
                                </button>
                                <button
                                    onClick={() => handleMaintenanceFilterClick('toSchedule')}
                                    className={`bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-3 text-center transition-all ${maintenanceFilter === 'toSchedule' ? 'ring-2 ring-red-400' : 'hover:ring-2 hover:ring-red-200'}`}
                                >
                                    <p className="text-2xl font-bold text-red-600">{maintenanceStats.toSchedule}</p>
                                    <p className="text-xs text-red-600/70 font-medium">Da Pianificare</p>
                                </button>
                                <button
                                    onClick={() => handleMaintenanceFilterClick('scheduled')}
                                    className={`bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 text-center transition-all ${maintenanceFilter === 'scheduled' ? 'ring-2 ring-green-400' : 'hover:ring-2 hover:ring-green-200'}`}
                                >
                                    <p className="text-2xl font-bold text-green-600">{maintenanceStats.scheduled}</p>
                                    <p className="text-xs text-green-600/70 font-medium">Pianificate</p>
                                </button>
                                <button
                                    onClick={() => handleMaintenanceFilterClick('closed')}
                                    className={`bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-3 text-center transition-all ${maintenanceFilter === 'closed' ? 'ring-2 ring-amber-400' : 'hover:ring-2 hover:ring-amber-200'}`}
                                >
                                    <p className="text-2xl font-bold text-amber-600">{maintenanceStats.closed}</p>
                                    <p className="text-xs text-amber-600/70 font-medium">Chiuse</p>
                                </button>
                                <button
                                    onClick={() => handleMaintenanceFilterClick('overdue')}
                                    className={`bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl p-3 text-center transition-all ${maintenanceFilter === 'overdue' ? 'ring-2 ring-orange-500' : 'hover:ring-2 hover:ring-orange-300'}`}
                                >
                                    <p className="text-2xl font-bold text-orange-700">{maintenanceStats.overdue}</p>
                                    <p className="text-xs text-orange-700/70 font-medium">Scaduti</p>
                                </button>
                            </div>

                            {/* Lista Attività Manutenzione Pianificate */}
                            <div
                                className={`space-y-2 ${showAllMaintenances ? 'max-h-[600px]' : 'max-h-[300px]'} pr-3 custom-scrollbar`}
                                style={{
                                    overflowY: 'scroll',
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#6b7280 #e5e7eb'
                                }}
                            >
                                {filteredMaintenances.length === 0 ? (
                                    <div className="text-center py-8 text-gray-400">
                                        <FontAwesomeIcon icon={faWrench} className="text-3xl mb-2" />
                                        <p className="text-sm">
                                            {maintenanceFilter === 'overdue'
                                                ? 'Nessuna attività scaduta da gestire'
                                                : maintenanceFilter === 'all'
                                                ? 'Nessuna attività di manutenzione per questo mese'
                                                : 'Nessuna attività con questo stato'}
                                        </p>
                                    </div>
                                ) : (
                                    (showAllMaintenances ? filteredMaintenances : filteredMaintenances.slice(0, 10)).map((action, index) => {
                                        // Convenzione colori: rosso da pianificare, verde pianificate, arancione chiuse
                                        const category = classifyMaintenance(action);
                                        const isClosed = category === 'closed';
                                        const isScheduled = category === 'scheduled';

                                        return (
                                            <div
                                                key={action._id || index}
                                                className={`p-4 rounded-lg border transition-all cursor-pointer hover:shadow-sm ${
                                                    isClosed 
                                                        ? 'bg-amber-50 border-amber-200 hover:border-amber-300' 
                                                        : isScheduled
                                                        ? 'bg-green-50 border-green-200 hover:border-green-300'
                                                        : 'bg-red-50 border-red-200 hover:border-red-300'
                                                }`}
                                                onClick={() => navigate(`/maintenance/edit/${action.maintenanceId}`)}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                            isClosed ? 'bg-amber-200' : isScheduled ? 'bg-green-200' : 'bg-red-200'
                                                        }`}>
                                                            <FontAwesomeIcon 
                                                                icon={isClosed ? faCheckCircle : isScheduled ? faCalendarCheck : faCalendarXmark} 
                                                                className={`text-base ${isClosed ? 'text-amber-700' : isScheduled ? 'text-green-700' : 'text-red-700'}`}
                                                            />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            {/* Codice manutenzione */}
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className="text-xs font-bold px-2 py-0.5 rounded bg-gray-200 text-gray-700">
                                                                    {action.maintenanceCode || 'N/D'}
                                                                </span>
                                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                                                    isClosed
                                                                        ? 'bg-amber-200 text-amber-700'
                                                                        : isScheduled
                                                                        ? 'bg-green-200 text-green-700'
                                                                        : 'bg-red-200 text-red-700'
                                                                }`}>
                                                                    {isClosed ? 'Chiusa' : action.statusLabel || (isScheduled ? 'Pianificata' : 'Da pianificare')}
                                                                </span>
                                                                {action.isOverdue && (
                                                                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-orange-200 text-orange-800">
                                                                        Scaduta {action.month}/{action.year}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {/* Titolo action */}
                                                            <p className="font-medium text-gray-800 text-sm line-clamp-2 mb-1">
                                                                {action.title || 'Attività manutenzione'}
                                                            </p>
                                                            {/* Cliente */}
                                                            <p className="text-xs text-gray-600 flex items-center gap-1 mb-0.5">
                                                                <FontAwesomeIcon icon={faUsers} className="text-gray-400 w-3" />
                                                                <span className="font-medium">{action.customerLabel || 'Cliente non specificato'}</span>
                                                            </p>
                                                            {/* Impianto e tipo */}
                                                            <p className="text-xs text-gray-600 flex items-center gap-1 mb-0.5">
                                                                <FontAwesomeIcon icon={faWrench} className="text-gray-400 w-3" />
                                                                <span>{action.systemLabel || 'Impianto non specificato'}</span>
                                                                {action.systemTypes && action.systemTypes.length > 0 && (
                                                                    <span className="text-gray-400">
                                                                        ({action.systemTypes.map(t => t.systemTypeLabel).join(', ')})
                                                                    </span>
                                                                )}
                                                            </p>
                                                            {/* Indirizzo impianto */}
                                                            {action.systemAddressLabel && (
                                                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 w-3" />
                                                                    <span>{action.systemAddressLabel}</span>
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                {/* Espande/comprime la lista: il conteggio segue sempre la card selezionata */}
                                {filteredMaintenances.length > 10 && (
                                    <button
                                        onClick={() => setShowAllMaintenances(prev => !prev)}
                                        className="w-full py-2 text-sm text-center rounded-lg transition-colors"
                                        style={{ color: palette.primary, backgroundColor: `${palette.primary}10` }}
                                    >
                                        {showAllMaintenances
                                            ? 'Mostra solo le prime 10'
                                            : `Vedi tutte le ${filteredMaintenances.length} manutenzioni${maintenanceFilterLabel ? ` ${maintenanceFilterLabel}` : ''}`}
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Box Contratti di Manutenzione in Scadenza */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <NavigationHeader
                        title="Contratti di Manutenzione in Scadenza"
                        subtitle={contractExpirationMonth.format('MMMM YYYY')}
                        onPrev={handleContractExpirationPrevMonth}
                        onNext={handleContractExpirationNextMonth}
                        onToday={handleContractExpirationToday}
                        icon={faCalendarXmark}
                    />

                    {loadingExpiringContracts ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            {/* Stats Card */}
                            <div className="grid grid-cols-1 gap-3 mb-5">
                                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-3 text-center">
                                    <p className="text-2xl font-bold text-orange-600">{expiringContracts.length}</p>
                                    <p className="text-xs text-orange-600/70 font-medium">Contratti in scadenza nel mese</p>
                                </div>
                            </div>

                            {/* Lista Contratti in Scadenza */}
                            <div 
                                className="space-y-2 max-h-[300px] pr-3 custom-scrollbar"
                                style={{
                                    overflowY: 'scroll',
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#6b7280 #e5e7eb'
                                }}
                            >
                                {expiringContracts.length === 0 ? (
                                    <div className="text-center py-8 text-gray-400">
                                        <FontAwesomeIcon icon={faCalendarCheck} className="text-3xl mb-2" />
                                        <p className="text-sm">Nessun contratto in scadenza questo mese</p>
                                    </div>
                                ) : (
                                    expiringContracts.map((contract, index) => {
                                        const endDate = moment(contract.contractEndDate);
                                        const today = moment();
                                        const isExpired = endDate.isSameOrBefore(today, 'day');
                                        const isExpiringSoon = !isExpired && endDate.diff(today, 'days') <= 7;
                                        
                                        return (
                                            <div
                                                key={contract._id || index}
                                                className={`p-4 rounded-lg border transition-all cursor-pointer hover:shadow-sm ${
                                                    isExpired 
                                                        ? 'bg-red-50 border-red-200 hover:border-red-300' 
                                                        : isExpiringSoon
                                                        ? 'bg-orange-50 border-orange-200 hover:border-orange-300'
                                                        : 'bg-yellow-50 border-yellow-200 hover:border-yellow-300'
                                                }`}
                                                onClick={() => navigate(`/maintenance/edit/${contract._id}`)}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                            isExpired ? 'bg-red-200' : isExpiringSoon ? 'bg-orange-200' : 'bg-yellow-200'
                                                        }`}>
                                                            <FontAwesomeIcon 
                                                                icon={isExpired ? faExclamationTriangle : faCalendarXmark} 
                                                                className={`text-base ${isExpired ? 'text-red-700' : isExpiringSoon ? 'text-orange-700' : 'text-yellow-700'}`}
                                                            />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            {/* Codice manutenzione */}
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className="text-xs font-bold px-2 py-0.5 rounded bg-gray-200 text-gray-700">
                                                                    {contract.code || 'N/D'}
                                                                </span>
                                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                                                    isExpired ? 'bg-red-200 text-red-700' : isExpiringSoon ? 'bg-orange-200 text-orange-700' : 'bg-yellow-200 text-yellow-700'
                                                                }`}>
                                                                    {endDate.format('DD/MM/YYYY')}
                                                                </span>
                                                            </div>
                                                            {/* Cliente */}
                                                            <p className="text-sm font-medium text-gray-800 truncate">
                                                                {contract.customerLabel || 'Cliente N/D'}
                                                            </p>
                                                            {/* Impianto */}
                                                            <p className="text-xs text-gray-500 truncate">
                                                                {contract.systemLabel || 'Impianto N/D'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Azioni gestione contratto */}
                                                <div className="mt-3 pt-3 border-t border-gray-200/60" onClick={(e) => e.stopPropagation()}>
                                                    {openRenewalMenuId === contract._id ? (
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="text-xs text-gray-500 mr-1">Esito:</span>
                                                            <button
                                                                disabled={updatingRenewalId === contract._id}
                                                                onClick={() => handleMarkContractRenewal(contract._id, 'rinnovato')}
                                                                className="px-2.5 py-1 text-xs font-medium rounded-md bg-green-100 text-green-700 hover:bg-green-200 transition-colors disabled:opacity-50"
                                                            >
                                                                Rinnovato
                                                            </button>
                                                            <button
                                                                disabled={updatingRenewalId === contract._id}
                                                                onClick={() => handleMarkContractRenewal(contract._id, 'non_rinnovato')}
                                                                className="px-2.5 py-1 text-xs font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-50"
                                                            >
                                                                Non rinnovato
                                                            </button>
                                                            <button
                                                                disabled={updatingRenewalId === contract._id}
                                                                onClick={() => handleMarkContractRenewal(contract._id, 'gestito')}
                                                                className="px-2.5 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-50"
                                                            >
                                                                Gestito
                                                            </button>
                                                            <button
                                                                disabled={updatingRenewalId === contract._id}
                                                                onClick={() => setOpenRenewalMenuId(null)}
                                                                className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                                                            >
                                                                Annulla
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => setOpenRenewalMenuId(contract._id)}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                                                        >
                                                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                                                            Segna come gestito
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Toggle "Mostra anche gestiti" */}
                            {handledContracts.length > 0 && (
                                <div className="mt-4">
                                    <button
                                        onClick={() => setShowHandledContracts(prev => !prev)}
                                        className="inline-flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                                    >
                                        <FontAwesomeIcon icon={showHandledContracts ? faChevronRight : faChevronRight} className={`transition-transform ${showHandledContracts ? 'rotate-90' : ''}`} />
                                        {showHandledContracts ? 'Nascondi' : 'Mostra'} contratti gia&apos; gestiti ({handledContracts.length})
                                    </button>

                                    {showHandledContracts && (
                                        <div className="mt-3 space-y-2">
                                            {handledContracts.map((contract, index) => {
                                                const endDate = moment(contract.contractEndDate);
                                                const renewal = contract.contractRenewal || {};
                                                const isRenewed = renewal.status === 'rinnovato';
                                                return (
                                                    <div
                                                        key={contract._id || `handled-${index}`}
                                                        className="p-3 rounded-lg border border-gray-200 bg-gray-50"
                                                    >
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div
                                                                className="flex-1 min-w-0 cursor-pointer"
                                                                onClick={() => navigate(`/maintenance/edit/${contract._id}`)}
                                                            >
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-gray-200 text-gray-700">
                                                                        {contract.code || 'N/D'}
                                                                    </span>
                                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                                                        isRenewed ? 'bg-green-100 text-green-700' : renewal.status === 'non_rinnovato' ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'
                                                                    }`}>
                                                                        {renewalStatusLabel(renewal.status)}
                                                                    </span>
                                                                    <span className="text-xs text-gray-400">
                                                                        {endDate.format('DD/MM/YYYY')}
                                                                    </span>
                                                                </div>
                                                                <p className="text-sm font-medium text-gray-700 truncate">
                                                                    {contract.customerLabel || 'Cliente N/D'}
                                                                </p>
                                                                <p className="text-xs text-gray-400">
                                                                    Gestito {renewal.handledDate ? `il ${moment(renewal.handledDate).format('DD/MM/YYYY')}` : ''}
                                                                    {renewal.handledByLabel ? ` da ${renewal.handledByLabel}` : ''}
                                                                </p>
                                                            </div>
                                                            <button
                                                                disabled={updatingRenewalId === contract._id}
                                                                onClick={() => handleUndoContractRenewal(contract._id)}
                                                                className="px-2.5 py-1 text-xs font-medium rounded-md text-gray-500 border border-gray-300 hover:bg-white hover:text-gray-700 transition-colors disabled:opacity-50 flex-shrink-0"
                                                            >
                                                                Annulla
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Riepilogo */}
                            {expiringContracts.length > 0 && (
                                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-2xl font-bold text-orange-600">
                                            {expiringContracts.length}
                                        </span>
                                        <span className="text-sm text-gray-500">contratti in scadenza</span>
                                    </div>
                                    <button
                                        onClick={() => navigate('/maintenances')}
                                        className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                                        style={{ 
                                            backgroundColor: palette.primary,
                                            color: palette.textOnPrimary || '#fff'
                                        }}
                                    >
                                        Vai alle Manutenzioni
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Box Commesse del Giorno - Full Width */}
                <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <NavigationHeader
                        title="Commesse Schedulate"
                        subtitle={jobsDate.format('dddd D MMMM YYYY')}
                        onPrev={handleJobsPrevDay}
                        onNext={handleJobsNextDay}
                        onToday={handleJobsToday}
                        icon={faBriefcase}
                    />

                    {loadingJobs ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            {scheduledJobs.length === 0 ? (
                                <div className="text-center py-12 text-gray-400">
                                    <FontAwesomeIcon icon={faBriefcase} className="text-4xl mb-3" />
                                    <p className="text-lg font-medium">Nessuna commessa schedulata</p>
                                    <p className="text-sm">per {jobsDate.format('dddd D MMMM')}</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                    {scheduledJobs.map((app, index) => (
                                        <div
                                            key={app._id || index}
                                            className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all cursor-pointer hover:border-gray-300"
                                            onClick={() => navigate(`/job/edit/${app.jobId}`)}
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <span 
                                                        className="px-2 py-1 text-xs font-bold rounded"
                                                        style={{ 
                                                            backgroundColor: palette.primary,
                                                            color: palette.textOnPrimary || '#fff'
                                                        }}
                                                    >
                                                        {app.jobCode || 'N/D'}
                                                    </span>
                                                    {app.jobTypeLabel && (
                                                        <span className="px-2 py-1 text-xs rounded font-medium bg-gray-100 text-gray-600">
                                                            {app.jobTypeLabel}
                                                        </span>
                                                    )}
                                                </div>
                                                <button
                                                    className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/job/edit/${app.jobId}`);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faEye} className="text-gray-500 text-sm" />
                                                </button>
                                            </div>

                                            <h4 className="font-semibold text-gray-800 mb-2 line-clamp-1">
                                                {app.customerLabel || app.title || 'Commessa'}
                                            </h4>

                                            {app.systemLabel && (
                                                <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400" />
                                                    <span className="line-clamp-1">{app.systemLabel}</span>
                                                </p>
                                            )}

                                            {/* Orario e operatori */}
                                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                                <div className="flex items-center gap-1.5 text-sm text-gray-500">
                                                    <FontAwesomeIcon icon={faClock} />
                                                    <span>
                                                        {moment(app.fromDate).format('HH:mm')}
                                                        {app.toDate && ` - ${moment(app.toDate).format('HH:mm')}`}
                                                    </span>
                                                </div>
                                                
                                                {app.users && app.users.length > 0 && (
                                                    <div className="flex items-center gap-1.5 text-sm text-gray-500">
                                                        <FontAwesomeIcon icon={faUsers} />
                                                        <span>{app.users.length} operatori</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Riepilogo giornata */}
                            {scheduledJobs.length > 0 && (
                                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                                    <div className="flex items-center gap-6">
                                        <div className="flex items-center gap-2">
                                            <span className="text-2xl font-bold" style={{ color: palette.primary }}>
                                                {scheduledJobs.length}
                                            </span>
                                            <span className="text-sm text-gray-500">commesse totali</span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => navigate('/jobsCalendar')}
                                        className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                                        style={{ 
                                            backgroundColor: palette.primary,
                                            color: palette.textOnPrimary || '#fff'
                                        }}
                                    >
                                        Vai al Calendario
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
